<script lang="ts">
	import { page } from '$app/state';
	import { userAccountStore, userRole } from '$lib/stores/auth-store';
	import { getDeploymentEnvironment } from '$lib/utils/get-deployment-environment';
	import {
		EarthEuropeAfrica,
		Catalog,
		GroupAccess,
		BatchJob,
		FetchUploadCloud,
		CicsDb2Connection,
		Airport_02,
		UserProfile,
		ChevronDown,
		Plug,
		Power,
		Folder
	} from 'carbon-icons-svelte';
	import type { SvelteComponent } from 'svelte';

	import MenuItem from './MenuItem.svelte';
	import { createEnum } from '$lib/utils/utils';

	import { menuOpen } from '$lib/stores/ui-store';
	import { PermissionService, type featureTypes } from '$lib/utils/permissions-helper';
	import SwitchProfileModal from '../modals/SwitchProfileModal.svelte';
	import { Portal, Dialog, Popover } from '@skeletonlabs/skeleton-svelte';

	let switchProfileDialogOpen = $state(false);

	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';

	//For better typesafety of groups. Not fully implemented
	const GROUPNAMES = createEnum({
		Navigasjon: 'Navigasjon',
		Admin: 'Admin',
		Profil: 'Profil'
	});

	type MenuItem = {
		title: string;
		url: string;
		visibleForRole: Array<'SuperAdmin' | 'Standardbruker' | 'Administrator'>;
		visibleInContext: Array<'Desktop' | 'Tablet'>;
		targetBlank?: boolean;
		group: keyof typeof GROUPNAMES;
		component?: Partial<SvelteComponent>;
		permission?: keyof typeof featureTypes;
	};

	const conditionalMenuItem: MenuItem[] = [
		{
			title: 'Luftfartshindere',
			url: '/dashboards/aviationobstacles',
			visibleForRole: ['Administrator', 'SuperAdmin'],
			visibleInContext: ['Desktop', 'Tablet'],
			group: GROUPNAMES.Navigasjon,
			targetBlank: false,
			component: Airport_02
		}
	];

	const menuItems: MenuItem[] = [
		{
			title: 'Kart',
			url: '/map',
			visibleForRole: ['Standardbruker', 'SuperAdmin', 'Administrator'],
			visibleInContext: ['Desktop', 'Tablet'],
			group: GROUPNAMES.Navigasjon,
			component: EarthEuropeAfrica
		},
		{
			title: 'SmartSkjema',
			url: '/condition-templates',
			visibleForRole: ['SuperAdmin', 'Standardbruker', 'Administrator'],
			visibleInContext: ['Desktop'],
			group: GROUPNAMES.Navigasjon,
			component: Catalog,
			permission: 'VEDLIKEHOLD_CREATE_TEMPLATE'
		},
		{
			title: 'Feltkatalog',
			url: '/smartdata',
			visibleForRole: ['Standardbruker', 'Administrator', 'SuperAdmin'],
			visibleInContext: ['Desktop', 'Tablet'],
			group: GROUPNAMES.Navigasjon,
			targetBlank: false,
			component: Folder
		},
		{
			title: $userAccountStore.roleName === 'Administrator' ? 'Mitt firma' : 'Brukere og firmaer',
			url: '/admin/companies',
			visibleForRole: ['SuperAdmin', 'Administrator'],
			visibleInContext: ['Desktop'],
			group:
				$userAccountStore.roleName === 'Administrator' ? GROUPNAMES.Navigasjon : GROUPNAMES.Admin,
			component: GroupAccess
		},
		{
			title: 'Min side',
			url: '/account/profile',
			visibleForRole: ['Standardbruker', 'Administrator', 'SuperAdmin'],
			visibleInContext: ['Desktop', 'Tablet'],
			component: UserProfile,
			group: GROUPNAMES.Profil
		},
		{
			title: 'Datahåndtering',
			url: '/admin/data-management',
			visibleForRole: ['SuperAdmin'],
			visibleInContext: ['Desktop'],
			component: FetchUploadCloud,
			group: GROUPNAMES.Admin
		},
		{
			title: 'Globale verdier',
			url: '/admin/globals',
			visibleForRole: ['SuperAdmin'],
			visibleInContext: ['Desktop'],
			group: GROUPNAMES.Admin,
			component: BatchJob
		},
		{
			title: 'Basedata stolpe',
			url: '/admin/base-data/pole',
			visibleForRole: ['SuperAdmin'],
			visibleInContext: ['Desktop'],
			group: GROUPNAMES.Admin,
			component: CicsDb2Connection
		},
		{
			title: 'Basedata ledning',
			url: '/admin/base-data/line',
			visibleForRole: ['SuperAdmin'],
			visibleInContext: ['Desktop'],
			group: GROUPNAMES.Admin,
			component: CicsDb2Connection
		},
		...(PermissionService.can('AVIATION_OBSTACLES') ? conditionalMenuItem : [])
	];

	// When supported, use this instead. Currently not working on mobile devices.
	// See https://stackoverflow.com/questions/77663053/how-to-enable-object-groupby-in-typescript-and-node-js
	// const menuItemsGrouped = Object.groupBy(menuItems, ({ group }) => group);

	const menuItemsGrouped = menuItems.reduce((container, menuItem) => {
		const { group } = menuItem;
		// @ts-ignore - Can be used as keys
		container[group] = container[group] ?? [];
		// @ts-ignore - Can be used as keys
		container[group].push(menuItem);
		return container;
	}, {});

	$effect(() => {
		page.url;
		$menuOpen = false;
	});
</script>

<aside
	class="fixed w-[420px] h-full bg-secondary shadow-lg top-0 left-0 z-[9000] max-h-screen {$menuOpen
		? 'open'
		: ''}"
>
	<nav class="h-full max-h-screen text-xl flex flex-col relative">
		<div class="p-8">
			<div class="flex flex-wrap items-center justify-center text-[#F8F9FC]">
				<div class="basis-full text-center">
					{#if $userAccountStore.picture}
						<img
							class="rounded-full h-16 w-16 object-fill mx-auto mb-2"
							src={$userAccountStore.picture}
							alt={$userAccountStore.fullname}
						/>
					{:else}
						<span
							class="inline-block mx-auto bg-white w-16 h-16 rounded-full relative text-secondary"
						>
							<UserProfile
								size={24}
								class=" absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
							/>
						</span>
					{/if}
				</div>
				<div class="text-center basis-full">
					<p class="text-sm font-medium">{$userAccountStore.fullname}</p>
					<p class="text-xs">{$userAccountStore.companyName}</p>
				</div>
				<Popover>
					<Popover.Trigger class="btn btn-sm btn-outline btn-white mt-3">
						Min profil <ChevronDown />
					</Popover.Trigger>
					<Popover.Positioner class="!z-[1]">
						<Popover.Content>
							<div class="bg-white px-4 py-3 w-72 shadow-xl rounded-md relative">
								{#each Object.getOwnPropertyNames(menuItemsGrouped) as groupName (groupName)}
									<MenuItem
										{menuItemsGrouped}
										{groupName}
										includeCategories={[GROUPNAMES.Profil]}
									/>
								{/each}

								<div class="pb-3 border-t border-secondary/10 font-medium">
									<Dialog
										open={switchProfileDialogOpen}
										onOpenChange={(e) => (switchProfileDialogOpen = e.open)}
									>
										<Dialog.Trigger
											class="items-center flex text-sm text-secondary hover:text-warningcontrast border-transparent border hover:border-warning hover:bg-warning/15 rounded-md w-full py-3 mt-4 px-1"
										>
											<Plug size={16} class="mr-3 scale-110" /> <span>Bytt innlogget firma</span>
										</Dialog.Trigger>
										<Portal>
											<Dialog.Backdrop class="fixed inset-0 bg-secondary/50" />
											<Dialog.Positioner
												class="fixed inset-0 z-50 flex justify-center items-center p-4"
											>
												<Dialog.Content
													class="!max-h-auto min-h-[300px] flex w-3xl max-w-full flex-col bg-white p-5 rounded-md"
												>
													{#if switchProfileDialogOpen}
														<SwitchProfileModal />
													{/if}
												</Dialog.Content>
											</Dialog.Positioner>
										</Portal>
									</Dialog>

									<button
										onclick={() => {
											//$menuOpen = false;
											goto(resolve('/logout'));
											return;
										}}
										title="Logg ut av SmartGIS"
										class="items-center flex text-sm text-secondary hover:text-warningcontrast border-transparent border hover:border-warning hover:bg-warning/15 rounded-md w-full py-3 mt-4 px-1"
										><Power size={16} class="mr-3 scale-110" /> <span>Logg ut</span>
									</button>
								</div>
							</div>
						</Popover.Content>
					</Popover.Positioner>
				</Popover>
			</div>
		</div>
		<div
			class="h-full flex flex-col justify-between from-white to-[#F8F9FC] bg-gradient-to-br backdrop-blur-md p-8 rounded-tr-xl rounded-tl-xl overflow-auto"
		>
			<!-- Mobile menu button -->
			<!--
				<div class="absolute inset-y-0 left-0 flex items-center 2xl:hidden">
					<button
						on:click|preventDefault={() => (showMobileMenu = !showMobileMenu)}
						type="button"
						class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-indigo-500"
						aria-controls="mobile-menu"
						aria-expanded={showMobileMenu}
					>
						<span class="sr-only">Open main menu</span>
						<svg
							class="block h-6 w-6"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 6h16M4 12h16M4 18h16"
							/>
						</svg>
					</button>
				</div>
			{/if}-->
			<div class="flex items-center justify-start">
				<div class="flex flex-col flex-wrap w-full">
					<div class="w-full flex flex-col flex-wrap">
						{#if $userRole}
							{#each Object.getOwnPropertyNames(menuItemsGrouped) as groupName (groupName)}
								<MenuItem
									{menuItemsGrouped}
									{groupName}
									includeCategories={[GROUPNAMES.Navigasjon, GROUPNAMES.Admin]}
								/>
							{/each}
						{/if}
					</div>
				</div>
			</div>
			<div class="flex flex-col flex-wrap justify-self-end mt-14">
				<h3 class="flex self-center gap-x-2 mb-4">
					<img alt="SmartGIS logo" class="h-16 w-auto" src="/images/smartgis-logo.png" />
					<sup class="text-error">{getDeploymentEnvironment().text}</sup>
				</h3>
			</div>
		</div>
	</nav>
	<button
		class="btn px-4 py-3 !rounded-full gap-x-2 duration-150 border-2 lg:hover:text-primary w-auto basis-[46px] self-start my-2 shadow-none bg-transparent min-w-[46px] !z-60 !min-h-[46px] h-[46px] absolute !p-0 ml-0 left-[350px] top-0 border-white text-white lg:hover:opacity-75 open"
		aria-label="Lukk meny"
		onclick={() => ($menuOpen = !$menuOpen)}
	>
		<svg class="stroke-secondary" width="24" height="24">
			<line id="top" x1="0" y1="4" x2="24" y2="4" />
			<line id="middle" x1="0" y1="12" x2="24" y2="12" />
			<line id="bottom" x1="0" y1="20" x2="24" y2="20" />
		</svg>
	</button>
</aside>

<style>
	aside {
		transform: translateX(-100%);
		transition: all 0.3s ease-in-out;
	}

	.open {
		transform: translateX(0);
	}
	svg {
		min-height: 24px;
		transition: transform 0.3s ease-in-out;
	}

	svg line {
		stroke: currentColor;
		stroke-width: 2;
		transition: transform 0.3s ease-in-out;
	}

	button {
		z-index: 20;
	}

	.open svg {
		transform: scale(0.7);
	}

	.open #top {
		stroke-width: 2;
		transform: translate(7px, 0px) rotate(45deg);
	}

	.open #middle {
		opacity: 0;
	}

	.open #bottom {
		stroke-width: 2;
		transform: translate(-10px, 6px) rotate(-45deg);
	}
</style>
